/**
 * Индикатор фоновых процессов в подвале рельса.
 *
 * Кнопка с бейджем (число активных синхронизаций) + Popover со списком job'ов:
 * прогресс, статус, отмена активных, скрытие завершённых. Рендерится только
 * когда есть что показать (по образцу Hermes-индикатора в AppLayout).
 */
import { Badge, Button, List, Popover, Progress, Tag, Tooltip, Typography } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';
import {
  CloseOutlined,
  LoadingOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { JobStatus } from '@lens/shared';
import { useJobs, type UiJob } from './JobsContext.tsx';

const { Text } = Typography;

const STATUS_META: Record<JobStatus, { label: string; color: string }> = {
  pending: { label: 'в очереди', color: 'default' },
  running: { label: 'выполняется', color: 'processing' },
  success: { label: 'готово', color: 'success' },
  error: { label: 'ошибка', color: 'error' },
  cancelled: { label: 'отменено', color: 'default' },
};

function jobPercent(job: UiJob): number {
  if (job.status === 'success') return 100;
  if (job.total <= 0) return 0;
  return Math.min(100, Math.round((job.progress / job.total) * 100));
}

function JobRow({ job }: { job: UiJob }): JSX.Element {
  const { cancel, dismiss } = useJobs();
  const meta = STATUS_META[job.status];
  const percent = jobPercent(job);
  return (
    <List.Item style={{ padding: '10px 0' }}>
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Text strong ellipsis style={{ flex: 1 }} title={job.label}>
            {job.label}
          </Text>
          <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>
            {meta.label}
          </Tag>
          {job.active ? (
            <Tooltip title="Отменить">
              <Button
                type="text"
                size="small"
                icon={<StopOutlined />}
                aria-label="Отменить задачу"
                onClick={() => void cancel(job.id)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Скрыть">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                aria-label="Скрыть задачу"
                onClick={() => dismiss(job.id)}
              />
            </Tooltip>
          )}
        </div>
        <Progress
          percent={percent}
          size="small"
          status={
            job.status === 'error'
              ? 'exception'
              : job.status === 'success'
                ? 'success'
                : job.active
                  ? 'active'
                  : 'normal'
          }
        />
        <div style={{ marginTop: 2 }}>
          {job.active ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {job.progress} из {job.total}
            </Text>
          ) : job.status === 'error' && job.error ? (
            <Text type="danger" style={{ fontSize: 12 }} title={job.error}>
              {job.error}
            </Text>
          ) : job.status === 'success' ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Обработано: {job.total}
            </Text>
          ) : null}
        </div>
      </div>
    </List.Item>
  );
}

export function JobsIndicator({
  className,
  placement = 'top',
}: {
  className?: string;
  placement?: TooltipPlacement;
}): JSX.Element | null {
  const { jobs, activeCount, open, showDock, closeDock } = useJobs();
  if (jobs.length === 0) return null;

  const tooltip =
    activeCount > 0
      ? `Фоновые синхронизации: ${activeCount}`
      : 'Фоновые синхронизации';

  const content = (
    <div style={{ width: 340, maxHeight: 420, overflowY: 'auto' }}>
      <List
        dataSource={jobs}
        split
        renderItem={(job) => <JobRow key={job.id} job={job} />}
      />
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(v) => (v ? showDock() : closeDock())}
      trigger="click"
      placement="rightBottom"
      title="Фоновые процессы"
      content={content}
    >
      <Tooltip title={tooltip} placement={placement} open={open ? false : undefined}>
        <button
          type="button"
          className={className}
          aria-label={tooltip}
          data-testid="jobs-indicator"
        >
          <Badge count={activeCount} size="small" dot={activeCount > 0}>
            {activeCount > 0 ? <LoadingOutlined /> : <SyncOutlined />}
          </Badge>
        </button>
      </Tooltip>
    </Popover>
  );
}
